import { useEffect, useMemo, useRef, useState } from "react";
import { MdCheck, MdEmojiEvents, MdGroups } from "react-icons/md";
import { BrandButton } from "../components/ui/BrandButton";
import { BrandSelect } from "../components/form/BrandSelect";
import { EmojiSymbol } from "../components/ui/EmojiSymbol";
import { LoginGiftModal } from "../components/home/LoginGiftModal";
import { getChampionPredictionOverview, getWorldCupFixtures, saveChampionPrediction } from "../services/worldCupService";
import { getPredictions } from "../services/predictionsService";
import { getRanking } from "../services/rankingService";
import { useAuth } from "../context/AuthContext";
import { getDaysUntilWorldCup } from "../utils/worldCupCountdown";
import { getTeamDisplayName, getTeamFlagEmoji } from "../utils/flagEmoji";
import { useAppLoading } from "../context/AppLoadingContext";

function formatPercent(value) {
  if (!Number.isFinite(value)) {
    return "0%";
  }

  return `${Math.round(value)}%`;
}

function formatPoints(points) {
  const safePoints = Number.isFinite(points) ? points : 0;
  return `${safePoints}`;
}

function formatRank(rank) {
  const safeRank = Number.isFinite(rank) ? rank : 0;
  return `#${safeRank}`;
}

function formatDateTime(dateValue) {
  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("es-UY", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatTime(dateValue) {
  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("es-UY", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
}

function formatDateLabel(dateValue) {
  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("es-UY", {
    weekday: "long",
    day: "2-digit",
    month: "short",
  }).format(date);
}

function formatLongDayMonth(dateValue) {
  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("es-UY", {
    day: "numeric",
    month: "long",
  }).format(date);
}

function getDateKey(dateValue) {
  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Montevideo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const parts = formatter.formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  const day = parts.find((part) => part.type === "day")?.value ?? "";

  return `${year}-${month}-${day}`;
}

function isUruguayMatch(fixture) {
  const homeTeam = String(fixture?.teams?.home?.name ?? "").toLowerCase();
  const awayTeam = String(fixture?.teams?.away?.name ?? "").toLowerCase();

  return homeTeam.includes("uruguay") || awayTeam.includes("uruguay");
}

function isPlaceholderTeamName(teamName) {
  const normalized = String(teamName ?? "").trim().toLowerCase();

  if (!normalized) {
    return true;
  }

  return [
    "tbd",
    "to be determined",
    "winner",
    "winner of",
    "loser",
    "loser of",
    "pending",
    "pendiente",
    "pendiente de definir",
    "to be announced",
    "unknown",
    "undefined",
  ].some((fragment) => normalized.includes(fragment));
}

function hasRenderableFixtureTeams(fixture) {
  return (
    fixture &&
    !isPlaceholderTeamName(fixture.teams?.home?.name) &&
    !isPlaceholderTeamName(fixture.teams?.away?.name)
  );
}

function getRenderablePhases(phases) {
  return (phases ?? [])
    .map((phase) => ({
      ...phase,
      fixtures: (phase.fixtures ?? []).filter(hasRenderableFixtureTeams),
    }))
    .filter((phase) => phase.fixtures.length > 0);
}

function countPendingPredictions(tournamentData) {
  const renderablePhases = getRenderablePhases(tournamentData?.phases);

  return renderablePhases.reduce(
    (total, phase) =>
      total + phase.fixtures.filter((fixture) => !fixture.prediction).length,
    0,
  );
}

function getFixtureInfo(fixture) {
  return {
    homeName: getTeamDisplayName(fixture?.teams?.home?.name),
    awayName: getTeamDisplayName(fixture?.teams?.away?.name),
    homeFlag: getTeamFlagEmoji(fixture?.teams?.home?.name) || "🏳️",
    awayFlag: getTeamFlagEmoji(fixture?.teams?.away?.name) || "🏳️",
  };
}

function SectionTitle({ icon, title, iconBgClassName = "bg-brand-gold-soft" }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${iconBgClassName} text-brand-primary sm:h-9 sm:w-9`}
      >
        {icon}
      </div>
      <h3 className="font-sans text-base font-bold text-brand-primary sm:text-lg">
        {title}
      </h3>
    </div>
  );
}

function TrophyIcon() {
  return (
    <MdEmojiEvents aria-hidden="true" className="h-4 w-4 text-brand-primary" />
  );
}

function PeopleIcon() {
  return <MdGroups aria-hidden="true" className="h-4 w-4 text-brand-primary" />;
}

function AccentLine() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 64 4"
      className="mt-4 block"
      width="56"
      height="3"
    >
      <rect x="0" y="0" width="56" height="3" rx="1.5" fill="#EBCD78" />
    </svg>
  );
}

function getFixtureLabel(fixture) {
  const info = getFixtureInfo(fixture);

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-2">
        <EmojiSymbol emoji={info.homeFlag} className="text-[1em] leading-none" />
        <span>{info.homeName}</span>
      </span>
      <span className="text-slate-400">vs</span>
      <span className="inline-flex items-center gap-2">
        <span>{info.awayName}</span>
        <EmojiSymbol emoji={info.awayFlag} className="text-[1em] leading-none" />
      </span>
    </span>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 shrink-0">
      <rect
        x="4"
        y="5"
        width="16"
        height="15"
        rx="3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
      />
      <path
        d="M4 9h16"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2.4"
      />
      <path
        d="M8 3.5v3.5M16 3.5v3.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2.4"
      />
      <rect x="7.2" y="11.8" width="1.8" height="1.8" rx="0.45" fill="currentColor" />
      <rect x="10.9" y="11.8" width="1.8" height="1.8" rx="0.45" fill="currentColor" />
      <rect x="14.6" y="11.8" width="1.8" height="1.8" rx="0.45" fill="currentColor" />
      <rect x="7.2" y="15.4" width="1.8" height="1.8" rx="0.45" fill="currentColor" />
      <rect x="10.9" y="15.4" width="1.8" height="1.8" rx="0.45" fill="currentColor" />
    </svg>
  );
}

export function HomePage() {
  const { user, prizePopup, acknowledgePrizePopupShown: acknowledgePrizePopupShownOnServer } = useAuth();
  const { trackAsync } = useAppLoading();
  const daysRemaining = getDaysUntilWorldCup();
  const isWorldCupStarted = daysRemaining === 0;
  const isRankedUser = user?.role?.name === "user";
  const [overview, setOverview] = useState(null);
  const [fixtures, setFixtures] = useState(null);
  const [viewerRanking, setViewerRanking] = useState(null);
  const [pendingPredictionsCount, setPendingPredictionsCount] = useState(null);
  const [selectedTeamApiId, setSelectedTeamApiId] = useState("");
  const [isSavingChampion, setIsSavingChampion] = useState(false);
  const [communityFlash, setCommunityFlash] = useState(false);
  const [communityTitleOverride, setCommunityTitleOverride] = useState("");
  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
  const [isAcknowledgingPrizePopup, setIsAcknowledgingPrizePopup] = useState(false);
  const [isPrizePopupAcknowledged, setIsPrizePopupAcknowledged] = useState(false);
  const [prizePopupError, setPrizePopupError] = useState("");
  const prizePopupAcknowledgementRequestedRef = useRef(false);
  const previousUserIdRef = useRef(null);
  const communityFlashTimeoutRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    if (prizePopup?.shouldShow) {
      console.log("[PrizePopup][frontend][home] modal should open", prizePopup);
      setIsGiftModalOpen(true);
      setIsPrizePopupAcknowledged(false);
      setPrizePopupError("");
      void registerPrizePopupShown();
    }
  }, [prizePopup?.shouldShow, user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setIsGiftModalOpen(false);
      setIsAcknowledgingPrizePopup(false);
      setIsPrizePopupAcknowledged(false);
      setPrizePopupError("");
      prizePopupAcknowledgementRequestedRef.current = false;
      previousUserIdRef.current = null;
      return;
    }

    if (previousUserIdRef.current && previousUserIdRef.current !== user.id) {
      setIsGiftModalOpen(false);
      setIsAcknowledgingPrizePopup(false);
      setIsPrizePopupAcknowledged(false);
      setPrizePopupError("");
      prizePopupAcknowledgementRequestedRef.current = false;
    }

    previousUserIdRef.current = user.id;
  }, [user?.id]);

  const registerPrizePopupShown = async () => {
    if (isAcknowledgingPrizePopup || prizePopupAcknowledgementRequestedRef.current) {
      console.log("[PrizePopup][frontend][home] ack skipped because request is in flight");
      return;
    }

    prizePopupAcknowledgementRequestedRef.current = true;
    setIsAcknowledgingPrizePopup(true);
    setPrizePopupError("");
    console.log("[PrizePopup][frontend][home] ack start");

    try {
      await acknowledgePrizePopupShownOnServer();
      console.log("[PrizePopup][frontend][home] ack success");
      setIsPrizePopupAcknowledged(true);
    } catch {
      console.log("[PrizePopup][frontend][home] ack failed");
      prizePopupAcknowledgementRequestedRef.current = false;
      setIsPrizePopupAcknowledged(false);
      setPrizePopupError("No pudimos registrar que viste el regalo. Volvé a intentarlo.");
    } finally {
      setIsAcknowledgingPrizePopup(false);
    }
  };

  const handleRetryPrizePopupAcknowledgement = async () => {
    await registerPrizePopupShown();
  };

  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      try {
        const requests = [
          getChampionPredictionOverview(),
          getWorldCupFixtures(),
        ];

        if (isWorldCupStarted) {
          requests.push(getPredictions());
        }

        const [championData, fixturesData, predictionsData] = await Promise.all(requests);

        if (!isMounted) {
          return;
        }

        setOverview(championData);
        setFixtures(fixturesData);
        setSelectedTeamApiId(
          championData?.prediction?.predictedTeamApiId
            ? String(championData.prediction.predictedTeamApiId)
            : "",
        );

        if (isWorldCupStarted) {
          setPendingPredictionsCount(countPendingPredictions(predictionsData));

          try {
            const rankingData = await getRanking({ pageSize: 1 });

            if (!isMounted) {
              return;
            }

            setViewerRanking(rankingData?.viewer ?? null);
          } catch {
            if (!isMounted) {
              return;
            }

            setViewerRanking(null);
          }
        } else {
          setViewerRanking(null);
          setPendingPredictionsCount(null);
        }
      } catch {
        if (!isMounted) {
          return;
        }

        setOverview({
          teams: [],
          stats: {
            teamBreakdown: [],
          },
        });
        setFixtures({
          phases: [],
        });
        setViewerRanking(null);
        setPendingPredictionsCount(null);
      }
    }

    void trackAsync(loadData);

    return () => {
      isMounted = false;
    };
  }, [isWorldCupStarted, trackAsync]);

  useEffect(
    () => () => {
      if (communityFlashTimeoutRef.current) {
        window.clearTimeout(communityFlashTimeoutRef.current);
      }
    },
    [],
  );

  const refreshChampionOverview = async () => {
    const championData = await getChampionPredictionOverview();
    setOverview(championData);
    setSelectedTeamApiId(
      championData?.prediction?.predictedTeamApiId
        ? String(championData.prediction.predictedTeamApiId)
        : "",
    );
  };

  const handleSaveChampion = async () => {
    if (!selectedTeamApiId || overview?.isLocked) {
      return;
    }

    try {
      setIsSavingChampion(true);

      await saveChampionPrediction({
        predictedTeamApiId: Number(selectedTeamApiId),
      });

      setCommunityFlash(true);
      setCommunityTitleOverride("La comunidad piensa que va a ganar…");

      if (communityFlashTimeoutRef.current) {
        window.clearTimeout(communityFlashTimeoutRef.current);
      }

      communityFlashTimeoutRef.current = window.setTimeout(() => {
        setCommunityFlash(false);
        setCommunityTitleOverride("");
      }, 2400);

      try {
        await refreshChampionOverview();
      } catch {
        // If the refresh fails, keep the optimistic selection visible.
      }
    } catch {
      // Keep the screen clean; the button state already reflects the failure path.
    } finally {
      setIsSavingChampion(false);
    }
  };

  const teamOptions = overview?.teams ?? [];
  const topCountries = useMemo(
    () => (overview?.stats?.teamBreakdown ?? []).slice(0, 4),
    [overview],
  );
  const longestCountryNameLength = useMemo(
    () =>
      topCountries.reduce((longest, team) => {
        const displayName = getTeamDisplayName(team.name);
        return Math.max(longest, displayName.length);
      }, 0),
    [topCountries],
  );

  const savedTeamApiId = overview?.prediction?.predictedTeamApiId
    ? String(overview.prediction.predictedTeamApiId)
    : "";
  const savedTeamName = overview?.prediction?.predictedTeamName ?? "";
  const isSelectionSaved = Boolean(savedTeamApiId && selectedTeamApiId === savedTeamApiId);
  const isSelectionDirty = Boolean(savedTeamApiId && selectedTeamApiId && selectedTeamApiId !== savedTeamApiId);

  const allFixtures = useMemo(
    () => (fixtures?.phases ?? []).flatMap((phase) => phase.fixtures ?? []),
    [fixtures],
  );

  const futureUruguayFixtures = useMemo(() => {
    const now = Date.now();

    return allFixtures
      .filter(isUruguayMatch)
      .filter((fixture) => {
        const kickoffAt = new Date(fixture.kickoffAt);

        return !Number.isNaN(kickoffAt.getTime()) && kickoffAt.getTime() >= now;
      })
      .sort((left, right) => new Date(left.kickoffAt).getTime() - new Date(right.kickoffAt).getTime());
  }, [allFixtures]);

  const upcomingDateGroups = useMemo(() => {
    const now = Date.now();
    const groupedByDate = new Map();

    for (const fixture of allFixtures) {
      const kickoffAt = new Date(fixture.kickoffAt);

      if (Number.isNaN(kickoffAt.getTime()) || kickoffAt.getTime() < now) {
        continue;
      }

      const dateKey = getDateKey(fixture.kickoffAt);

      if (!groupedByDate.has(dateKey)) {
        groupedByDate.set(dateKey, []);
      }

      groupedByDate.get(dateKey).push(fixture);
    }

    return Array.from(groupedByDate.entries())
      .slice(0, 2)
      .map(([dateKey, fixturesForDate]) => ({
        dateKey,
        dateLabel: formatDateLabel(fixturesForDate[0]?.kickoffAt),
        fixtures: fixturesForDate.sort(
          (left, right) => new Date(left.kickoffAt).getTime() - new Date(right.kickoffAt).getTime(),
        ),
      }));
  }, [allFixtures]);

  const compactUruguayFixtures = futureUruguayFixtures.slice(0, 3);
  const compactUpcomingFixtures = useMemo(() => {
    const now = Date.now();

    return allFixtures
      .filter((fixture) => {
        const kickoffAt = new Date(fixture.kickoffAt);

        return !Number.isNaN(kickoffAt.getTime()) && kickoffAt.getTime() >= now;
      })
      .sort((left, right) => new Date(left.kickoffAt).getTime() - new Date(right.kickoffAt).getTime())
      .slice(0, 4);
  }, [allFixtures]);

  const handleClosePrizePopup = () => {
    if (isPrizePopupAcknowledged) {
      setIsGiftModalOpen(false);
    }
  };

  const communityCardTitle =
    communityTitleOverride || "La comunidad piensa que va a ganar…";

  return (
    <>
      <div className="grid gap-4 lg:h-full lg:min-h-0 lg:grid-rows-[auto_minmax(0,1fr)_minmax(0,1fr)]">
        <section className="px-1 py-1 sm:px-2 sm:py-2">
          <h2 className="font-display text-2xl text-brand-primary sm:text-3xl">Inicio</h2>
        </section>

        <section className="grid gap-4 lg:min-h-0 xl:grid-cols-3">
          {isWorldCupStarted ? (
            <DashboardCard className="min-h-[200px] lg:h-full lg:min-h-0" scrollable={false}>
              <SectionTitle icon={<CalendarIcon />} title="Resultados pendientes para cargar" />

              <div className="mt-6 flex items-end gap-3">
                <span
                  className="font-bold text-brand-primary"
                  style={{ fontSize: "clamp(3.2rem, 6vw, 4.8rem)", lineHeight: "1" }}
                >
                  {pendingPredictionsCount ?? "—"}
                </span>
                <span className="pb-2 text-xl font-bold text-brand-primary sm:text-2xl">
                  partidos
                </span>
              </div>

              <AccentLine />

              <p className="mt-3 max-w-xs text-[0.82rem] text-slate-600 sm:text-[0.9rem]">
                pendientes por cargar ahora mismo.
              </p>
            </DashboardCard>
          ) : (
            <DashboardCard className="min-h-[200px] lg:h-full lg:min-h-0">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-gold-soft text-brand-primary shadow-[inset_0_0_0_1px_rgba(18,55,166,0.08)]">
                  <CalendarIcon />
                </div>
                <p className="text-[0.62rem] font-bold uppercase tracking-[0.24em] text-brand-primary/80 sm:text-[0.66rem]">
                  Faltan
                </p>
              </div>

              <div className="mt-5 flex items-end gap-3">
                <span
                  className="font-bold text-brand-primary"
                  style={{ fontSize: "clamp(3.2rem, 6vw, 4.8rem)", lineHeight: "1" }}
                >
                  {daysRemaining}
                </span>
                <span className="pb-2 text-xl font-bold text-brand-primary sm:text-2xl">
                  días
                </span>
              </div>

              <AccentLine />

              <p className="mt-3 max-w-xs text-[0.82rem] text-slate-600 sm:text-[0.9rem]">
                para que arranque el mundial 😱
              </p>
            </DashboardCard>
          )}

          {isWorldCupStarted ? (
            <DashboardCard className="min-h-[200px] lg:h-full lg:min-h-0">
              <SectionTitle
                icon={<TrophyIcon />}
                title={"Tus puntos y posición"}
              />
              {isRankedUser ? (
                <>
                  <p className="mt-2 max-w-xl text-[0.82rem] leading-6 text-slate-600 sm:text-[0.9rem]">
                    Así vas en la tabla general del torneo.
                  </p>

                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    <article className="rounded-[1.4rem] border border-brand-accent-light bg-white px-4 py-4">
                      <p className="text-[0.7rem] font-bold uppercase tracking-[0.18em] text-slate-500">
                        Puntos
                      </p>
                      <p className="mt-2 font-display text-4xl text-brand-primary">
                        {viewerRanking ? formatPoints(viewerRanking.points) : "—"}
                      </p>
                    </article>

                    <article className="rounded-[1.4rem] border border-brand-accent-light bg-white px-4 py-4">
                      <p className="text-[0.7rem] font-bold uppercase tracking-[0.18em] text-slate-500">
                        Posición
                      </p>
                      <p className="mt-2 font-display text-4xl text-brand-primary">
                        {viewerRanking ? formatRank(viewerRanking.rank) : "—"}
                      </p>
                    </article>
                  </div>

                  {!viewerRanking ? (
                    <p className="mt-3 text-sm text-slate-500">
                      Todavía no pudimos cargar tu posición.
                    </p>
                  ) : null}
                </>
              ) : (
                <div className="mt-3 rounded-[1.4rem] border border-brand-accent-light bg-brand-surface px-4 py-4">
                  <p className="text-[0.82rem] leading-6 text-slate-600 sm:text-[0.9rem]">
                    Tu usuario tiene acceso de administración, así que no figura en la tabla general
                    de puntos y posición.
                  </p>
                </div>
              )}
            </DashboardCard>
          ) : (
            <DashboardCard className="min-h-[200px] lg:h-full lg:min-h-0">
              <SectionTitle icon={<TrophyIcon />} title="Elegí tu ganador" />
              <p className="mt-2 max-w-xl text-[0.82rem] leading-6 text-slate-600 sm:text-[0.9rem]">
                Acertá el campeón y sumá más puntos.
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <StatusPill tone={isSelectionSaved ? "primary" : "neutral"}>
                  {isSelectionSaved
                    ? `Ganador seleccionado: ${getTeamDisplayName(savedTeamName)}`
                    : "Todavía no guardaste un campeón"}
                </StatusPill>
                {isSelectionDirty ? <StatusPill tone="warning">Cambios sin guardar</StatusPill> : null}
              </div>

              <div className="mt-5 grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3">
                <BrandSelect
                  name="champion"
                  value={selectedTeamApiId}
                  onChange={(event) => setSelectedTeamApiId(event.target.value)}
                  options={[
                    { value: "", label: "Elegir ganador" },
                    ...teamOptions.map((team) => ({
                      value: team.apiId,
                      label: getTeamDisplayName(team.name),
                    })),
                  ]}
                  placeholder="Elegir ganador"
                  disabled={overview?.isLocked || teamOptions.length === 0 || isSavingChampion}
                  className="mt-0"
                  disableHoverEffects
                />

                <BrandButton
                  type="button"
                  variant="primary"
                  radius="2xl"
                  size="icon"
                  motion="none"
                  className="flex h-14 w-14 items-center justify-center border bg-brand-gold-soft text-brand-primary shadow-none hover:bg-brand-gold-soft"
                  onClick={() => void handleSaveChampion()}
                  disabled={
                    overview?.isLocked ||
                    teamOptions.length === 0 ||
                    !selectedTeamApiId ||
                    isSavingChampion
                  }
                  aria-label={isSavingChampion ? "Guardando campeón" : "Guardar campeón"}
                >
                  <MdCheck aria-hidden="true" className="h-6 w-6" />
                </BrandButton>
              </div>
            </DashboardCard>
          )}

          <DashboardCard className="min-h-[200px] lg:h-full lg:min-h-0">
            <SectionTitle icon={<CalendarIcon />} title="¿Cuándo juega Uruguay?" />

            <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
              {compactUruguayFixtures.length > 0 ? (
                compactUruguayFixtures.map((fixture) => (
                  <article
                    key={fixture.id}
                    className="flex items-center gap-3 rounded-[1.4rem] border border-brand-accent-light bg-white px-3 py-3 shadow-sm sm:px-3.5"
                  >
                    <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-brand-accent-light text-brand-primary sm:h-10 sm:w-10">
                      <CalendarIcon />
                    </div>
                    <div className="min-w-0">
                      <p className="font-sans text-[0.8rem] font-bold leading-none text-brand-primary sm:text-[0.86rem]">
                        {formatLongDayMonth(fixture.kickoffAt)}
                      </p>
                      <p className="mt-1 text-[0.68rem] leading-none text-slate-600 sm:text-[0.76rem]">
                        {formatTime(fixture.kickoffAt)}
                      </p>
                    </div>
                  </article>
                ))
              ) : (
                <p className="text-sm text-slate-500">Uruguay ya no tiene partidos en este mundial. ¡Nos vemos en el 2030!</p>
              )}
            </div>
          </DashboardCard>
        </section>

        <section className="grid gap-3 lg:min-h-0 lg:items-stretch xl:grid-cols-[0.8fr_1.2fr]">
          <DashboardCard className="lg:h-full lg:min-h-0">
            <SectionTitle
              icon={<PeopleIcon />}
              title={communityCardTitle}
              iconBgClassName={communityFlash ? "bg-brand-primary/10" : "bg-brand-gold-soft"}
            />

            <p className="mt-2 text-[0.82rem] leading-6 text-slate-600 sm:text-[0.9rem]">
              Lo más elegido por la comunidad en este momento.
            </p>

            <div className="mt-4 grid gap-3">
              {topCountries.length > 0 ? (
                topCountries.map((team) => {
                  const percentage = team.percentage ?? 0;

                  return (
                    <article
                      key={team.apiId}
                      className="grid items-center gap-x-3 gap-y-2 py-1.5"
                      style={{
                        gridTemplateColumns: `auto ${Math.max(longestCountryNameLength + 1, 8)}ch minmax(0, 1fr) auto`,
                      }}
                    >
                      <EmojiSymbol
                        emoji={getTeamFlagEmoji(team.name) || "🏳️"}
                        className="text-xl leading-none"
                      />
                      <span className="min-w-0 font-sans text-[0.82rem] font-bold leading-tight text-brand-primary sm:text-[0.9rem]">
                        {getTeamDisplayName(team.name)}
                      </span>
                      <div className="h-2 overflow-hidden rounded-full bg-brand-primary/10">
                        <div
                          className="h-full rounded-full bg-brand-primary"
                          style={{
                            width: `${Math.max(percentage, 3)}%`,
                          }}
                        />
                      </div>
                      <span className="font-sans text-[0.82rem] font-bold text-brand-primary sm:text-[0.9rem]">
                        {formatPercent(percentage)}
                      </span>
                    </article>
                  );
                })
              ) : (
                <p className="text-sm text-slate-500">Todavía no hay predicciones comunitarias.</p>
              )}
            </div>
          </DashboardCard>

          <DashboardCard className="lg:h-full lg:min-h-0">
            <SectionTitle icon={<CalendarIcon />} title="Próximos partidos" />

            <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
              {compactUpcomingFixtures.length > 0 ? (
                compactUpcomingFixtures.map((fixture) => (
                  <article
                    key={fixture.id}
                    className="rounded-2xl border border-brand-accent-light bg-white p-2 sm:p-3"
                  >
                    <p className="text-[0.74rem] font-semibold leading-tight text-brand-primary sm:text-[0.82rem]">
                      {getFixtureLabel(fixture)}
                    </p>
                    <p className="mt-1 text-[0.66rem] text-slate-600 sm:text-[0.74rem]">
                      {formatDateTime(fixture.kickoffAt)}
                    </p>
                  </article>
                ))
              ) : (
                <p className="text-sm text-slate-500">Todavía no hay próximos partidos para mostrar.</p>
              )}
            </div>
          </DashboardCard>
        </section>
      </div>
      <LoginGiftModal
        open={isGiftModalOpen}
        onClose={handleClosePrizePopup}
        onRetry={handleRetryPrizePopupAcknowledgement}
        canClose={isPrizePopupAcknowledged}
        isSaving={isAcknowledgingPrizePopup}
        errorMessage={prizePopupError}
      />
    </>
  );
}

function DashboardCard({ children, className = "", scrollable = true }) {
  return (
    <article
      className={`flex h-full min-h-0 flex-col overflow-hidden rounded-[2rem] border border-[#eadfb0] bg-white p-4 sm:p-5 lg:p-6 ${className}`.trim()}
    >
      <div className={`min-h-0 flex-1 pr-1 ${scrollable ? "overflow-y-auto" : "overflow-y-visible"}`}>
        {children}
      </div>
    </article>
  );
}

function StatusPill({ children, tone = "neutral" }) {
  const toneClassName =
    tone === "primary"
      ? "border-brand-primary/15 bg-brand-primary/10 text-brand-primary"
      : tone === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : tone === "warning"
        ? "border-amber-200 bg-amber-50 text-amber-700"
        : "border-brand-primary/10 bg-brand-surface text-brand-primary";

  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[0.72rem] font-semibold ${toneClassName}`}>
      {children}
    </span>
  );
}
